import { FormEvent, useEffect, useState } from "react";
import useSocket from "@/socket/useSocket";
import { useSocketActions } from "@/store/socketStore";
import { Message } from "@/types/chatTypes";
import toast from "react-hot-toast";
import useMethodStore, { useMethodActions } from "@/store/MethodStore";
import { FaArrowUp } from "react-icons/fa";
import useSettingsStore from "@/store/SettingsStore";
import { IoSettings } from "react-icons/io5";
import TooltipContainer from "../TooltipContainer";

function Chat() {
  const socket = useSocket();
  const { setSocketId, addChat, setRoom } = useSocketActions();
  const { setSettingsState, setFocusedWindow } = useMethodActions(); // Chat app state
  const chatState = useMethodStore(state => state.chatState)
  const setActiveTab = useSettingsStore(state => state.setActiveTab)
  const isChatNotificationOn = useSettingsStore(state => state.isChatNotificationOn)
  const colorPalette = useSettingsStore(state => state.colorPalette)

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState<string>("");
  const [username, setUsername] = useState<string>("");

  useEffect(() => {
    const handleConnect = () => {
      setSocketId(socket.id || "");

      // Inform server of username on connect
      if (username) {
        socket.emit("set-username", username);
      }
    };

    const setRandomUsername = (username: string) => {
      if (!username) return;
      setUsername(username);
    };

    const handleReceiveMessage = (data: Message) => {
      setMessages((prev) => [...prev, data]); // Update local messages
      addChat(data); // Update global state (if necessary)
      if (data.sender !== username && isChatNotificationOn) toast(`${data.sender}: ${data.message}`);
    };

    const setGlobalChatMessages = (globalChatMessages: Message[]) => {
      if (!globalChatMessages) return;
      setMessages(globalChatMessages);
    };

    const handleDisconnect = () => {
      setSocketId("");
      setRoom(null);
      toast("Disconnected from the server");
    };

    const handleConnectError = (error: unknown) => {
      if (error instanceof Error) {
        console.error("Connection Error:", error.message);
        toast(`Connection Error: ${error.message}`);
      } else {
        console.error("Connection Error:", error);
        toast("Connection Error occurred");
      }
    };

    // Listen for socket events
    socket.on("connect", handleConnect);
    socket.on("set-username", setRandomUsername);
    socket.on("all-global-messages", setGlobalChatMessages);
    socket.on("receive-message", handleReceiveMessage);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("set-username", setRandomUsername);
      socket.off("all-global-messages", setGlobalChatMessages);
      socket.off("receive-message", handleReceiveMessage);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
    };
  }, [socket, setSocketId, addChat, setRoom, username, isChatNotificationOn]);

  // Manage room joining/leaving based on chatState
  useEffect(() => {
    if (chatState !== "closed") {
      socket.emit("join-room", { room: "global" });
      toast("Joined the global chat room");
    } else {
      if (socket.id && username) {
        socket.emit("leave-room", { room: "global" });
        toast("Left the global chat room");
      }
    }

    return () => {
      if (chatState === "closed") {
        socket.emit("leave-room", { room: "global" });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatState, socket]);

  // Scroll to the latest message when new ones come in
  useEffect(() => {
    const messageBox = document.getElementById('chat-component'); // Get the message container
    if (messageBox) {
      messageBox.scrollTop = messageBox.scrollHeight; // Scroll to the latest message
    }
  }, [messages]);

  const sendMessage = (e: FormEvent) => {
    e.preventDefault();
    if (inputMessage.trim()) {
      try {
        socket.emit("message", {
          room: "global",
          message: inputMessage,
          sender: username || "Anonymous",
        });
        setInputMessage("");
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error: unknown) {
        toast.error("Failed to send message. Please try again.");
      }
    }
  };

  return chatState === "closed" ? null : (
    <div className="px-2 w-full h-full md:w-[400px] md:h-[600px] backdrop-s backdrop-blur-md text-zinc-900 bg-zinc-200/90 dark:text-zinc-100 dark:bg-zinc-900/90">
      <div className="flex p-3 justify-between items-center border-b-1 border-zinc-700/70">
        <h2 className="text-lg font-semibold">You joined as {username || "Anonymous"}</h2>
        <TooltipContainer tooltip="Settings">
          <div onClick={(e) => {
            e.stopPropagation()
            setSettingsState("floating")
            setActiveTab("chat")
            setFocusedWindow("Settings")
          }} className="text-2xl p-2 dark:text-zinc-400 dark:hover:text-zinc-100 text-zinc-800 hover:text-zinc-950"><IoSettings /></div>
        </TooltipContainer>
      </div>

      <div id="chat-component" className="h-[80%] md:h-[470px] w-full flex flex-col scroll2 py-3 px-1 overflow-y-auto">
        {messages.length > 0 ? (
          messages.map(({ message, sender, _id }, index) => {
            const isNewSender =
              index === 0 || messages[index - 1].sender !== sender;

            return (
              <div key={_id || index} className="flex flex-col items-start">
                {/* Display the username only if it's a new sender */}
                {isNewSender && (
                  <span className="text-sm pt-4 font-bold text-gray-700 dark:text-gray-200 mb-1">
                    {sender === username ? "You" : sender}
                  </span>
                )}
                <div
                  className={`flex items-center space-x-2 max-w-[75%] text-zinc-800 dark:text-zinc-200`}
                >
                  <span style={{
                    backgroundColor: sender === username ? colorPalette : "#3b82f6"
                  }} className={`h-full w-0.5`}></span>
                  <span>{message}</span>
                </div>
              </div>
            );
          })
        ) : (
          <div className="h-full flex justify-center items-center">
            <span>No messages yet</span>
          </div>
        )}
      </div>

      <div className="flex justify-center items-center py-3">
        <form onSubmit={sendMessage} className="input-area rounded-full flex justify-center items-center">
          <input
            className="w-72 text-zinc-900 bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-100 h-10 px-6 rounded-s-full"
            type="text"
            placeholder={`Send message as ${username || "Anonymous"}...`}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
          />
          <button disabled={!inputMessage} className="rounded-e-full bg-zinc-300 hover:bg-zinc-300/70 disabled:hover:bg-zinc-300 disabled:opacity-50 dark:bg-zinc-700 dark:disabled:opacity-50 dark:disabled:hover:bg-zinc-700 disabled:active:scale-100 active:scale-95 transition-all duration-300 dark:hover:bg-zinc-700/70 h-10 px-6" type="submit"><FaArrowUp /></button>
        </form>
      </div>
    </div>
  );
}

export default Chat;
